// Shared admin activity-log renderer. Used by the global /activity feed and by
// the entity-scoped "Hoạt động" views on the org- and account-detail pages.
// Presentational only: the parent owns data fetching + pagination so it can
// scope the feed (by orgId / actorUserId) however it needs.

import * as React from "react";
import { useTranslations } from "next-intl";
import { formatDateTimeVn } from "@chotca/ui";
import { formatActivityAction } from "@chotca/config";
import { DataTable, type Column } from "@/components/data-table";

const META_KEY_LABELS: Record<string, string> = {
  title: "Tiêu đề",
  name: "Tên",
  state: "Trạng thái",
  pack: "Gói",
  credits: "Credits",
  amountVnd: "Số tiền",
  amount: "Số tiền",
  price: "Giá",
  reason: "Lý do",
  status: "Trạng thái",
  email: "Email",
  phone: "SĐT",
  role: "Vai trò",
  postingId: "Tin",
  orgId: "Doanh nghiệp",
  userId: "Người dùng",
  count: "Số lượng",
};

const VND_KEYS = new Set(["amountVnd", "amount", "price", "totalVnd"]);

function formatMetaValue(key: string, value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "Có" : "Không";
  if (typeof value === "number") {
    if (VND_KEYS.has(key))
      return new Intl.NumberFormat("vi-VN").format(value) + "₫";
    return new Intl.NumberFormat("vi-VN").format(value);
  }
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

export function MetadataCell({
  metadata,
}: {
  metadata?: Record<string, unknown> | null;
}) {
  const [open, setOpen] = React.useState(false);
  if (!metadata || typeof metadata !== "object") {
    return <span className="text-xs text-ash">—</span>;
  }
  const entries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== "",
  );
  if (entries.length === 0) {
    return <span className="text-xs text-ash">—</span>;
  }
  return (
    <div className="max-w-80">
      <div className="flex flex-wrap gap-x-2 gap-y-1">
        {entries.map(([k, v]) => {
          const label = META_KEY_LABELS[k] ?? k;
          const display = formatMetaValue(k, v);
          return (
            <span key={k} className="text-xs text-ink">
              <span className="text-ash">{label}:</span>{" "}
              <span
                className={
                  k === "title" || k === "name"
                    ? "font-medium"
                    : VND_KEYS.has(k)
                      ? "font-mono tabular-nums"
                      : ""
                }
                title={display}
              >
                {display.length > 60 ? display.slice(0, 60) + "…" : display}
              </span>
            </span>
          );
        })}
      </div>
      {entries.length > 3 || JSON.stringify(metadata).length > 80 ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-1 text-[10px] uppercase tracking-wide text-ash hover:text-ink"
        >
          {open ? "Ẩn JSON" : "Xem JSON"}
        </button>
      ) : null}
      {open ? (
        <pre className="mt-1 max-h-40 overflow-auto rounded bg-mist p-2 text-[11px] leading-tight text-ink">
          {JSON.stringify(metadata, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

export type ActivityRow = {
  id: string;
  actorUserId: string | null;
  actorRole: string;
  action: string;
  createdAt: string;
  targetType?: string | null;
  targetId?: string | null;
  metadata?: Record<string, unknown> | null;
};

export function ActivityLogTable({
  rows,
  isLoading,
  isFetching,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  hideActor = false,
  emptyText,
}: {
  rows: ActivityRow[];
  isLoading?: boolean;
  isFetching?: boolean;
  page: number;
  pageSize: number;
  total?: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
  // On a single-actor view (a worker's own feed) the actor column is noise.
  hideActor?: boolean;
  emptyText?: string;
}) {
  const t = useTranslations("admin");

  const columns: Column<ActivityRow>[] = [
    {
      key: "time",
      header: "Thời gian",
      cell: (r) => (
        <span className="whitespace-nowrap text-xs">
          {formatDateTimeVn(new Date(r.createdAt))}
        </span>
      ),
    },
    ...(hideActor
      ? []
      : [
          {
            key: "actor",
            header: t("colActorRole"),
            cell: (r: ActivityRow) => (
              <span className="text-xs font-mono">
                {t(`actorRole_${r.actorRole}` as Parameters<typeof t>[0]) ??
                  r.actorRole}{" "}
                <span className="text-ash">
                  {r.actorUserId ? r.actorUserId.slice(0, 8) : ""}
                </span>
              </span>
            ),
          } as Column<ActivityRow>,
        ]),
    {
      key: "action",
      header: t("colAction"),
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">
            {formatActivityAction(r.action)}
          </p>
          <p className="truncate font-mono text-[10px] text-ink/40">
            {r.action}
          </p>
        </div>
      ),
    },
    {
      key: "entity",
      header: t("colEntity"),
      cell: (r) => (
        <span className="text-xs">
          {r.targetType ?? "—"}
          {r.targetId ? (
            <span className="ml-1 text-ash">{r.targetId.slice(0, 8)}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "meta",
      header: t("colNote"),
      cell: (r) => <MetadataCell metadata={r.metadata} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={isLoading}
      isFetching={isFetching}
      rowKey={(r) => r.id}
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyText={emptyText}
    />
  );
}
